import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS } from '../lib/styleGuide';
import { 
  Search, 
  MoreCircle, 
  Document, 
  Show,
  ChevronRight
} from 'react-iconly';
import { BottomNavigation } from '../components/BottomNavigation';
import { BackButton } from '../components/ui';
import { SellerHeader } from '../components/SellerHeader';
import { 
  getOrderMessagesByOrderId, 
  sendOrderMessage, 
  subscribeToOrderMessages,
  getUniqueOrdersFromMessages 
} from '../lib/messagesService';
import { getSellerMerchantOrders, getGroupStatus, updateGroupStatus, generateGroupId } from '../utils/multiVendorOrderApi';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { formatTimestamp } from '../lib/dateUtils';

export function SellerMessages() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  
  // Debug logging
  // SellerMessages component loaded
  
  // State for chat management
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading] = useState(false);
  const [sending, setSending] = useState(false);
  const [actualOrderData, setActualOrderData] = useState(null);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState('chats'); // 'chats', 'chat', 'order'
  const [selectedChatId, setSelectedChatId] = useState(null);

  // Get selected chat data
  const selectedChatData = chats.find(chat => chat.orderId === selectedChat);
  
  // Function to fetch actual order data
  const fetchOrderData = async (orderId) => {
    if (!orderId || !currentUser?.uid) return;
    
    try {
      console.log('🔄 UPDATED CODE - Fetching order data for orderId:', orderId);
      console.log('🔍 Current selectedChat:', selectedChat);
      console.log('🔍 Available chats:', chats.map(c => ({ orderId: c.orderId, customerName: c.customerName })));
      
      // Get the merchant order document directly
      console.log('🔍 Attempting to fetch merchant order document:', orderId);
      const merchantOrderRef = doc(db, 'merchant_orders', orderId);
      console.log('🔍 Document reference created:', merchantOrderRef.path);
      
      let merchantOrder = null;
      
      try {
        const merchantOrderSnap = await getDoc(merchantOrderRef);
        console.log('🔍 Document fetch result - exists:', merchantOrderSnap.exists());
        
        if (merchantOrderSnap.exists()) {
          merchantOrder = { id: merchantOrderSnap.id, ...merchantOrderSnap.data() };
          console.log('📦 Merchant order found:', merchantOrder);
          console.log('📦 Merchant order ID:', merchantOrder.id);
          console.log('📦 Merchant order sellerId:', merchantOrder.sellerId);
          console.log('📦 Current user ID:', currentUser?.uid);
          console.log('📦 Seller ID match:', merchantOrder.sellerId === currentUser?.uid);
        }
      } catch (directFetchError) {
        console.log('🔍 Direct fetch failed with error:', directFetchError.message);
      }
      
      // If direct fetch failed or document doesn't exist, this might be a parent order ID
      if (!merchantOrder) {
        console.log('🔍 Direct fetch failed, searching for merchant order with parentOrderId:', orderId);
        const merchantOrdersQuery = query(
          collection(db, 'merchant_orders'),
          where('parentOrderId', '==', orderId),
          where('sellerId', '==', currentUser?.uid)
        );
        const merchantOrdersSnap = await getDocs(merchantOrdersQuery);
        
        if (!merchantOrdersSnap.empty) {
          const merchantOrderDoc = merchantOrdersSnap.docs[0];
          merchantOrder = { id: merchantOrderDoc.id, ...merchantOrderDoc.data() };
          console.log('📦 Found merchant order by parentOrderId:', merchantOrder);
          console.log('📦 Merchant order ID:', merchantOrder.id);
          console.log('📦 Merchant order sellerId:', merchantOrder.sellerId);
          console.log('📦 Current user ID:', currentUser?.uid);
          console.log('📦 Seller ID match:', merchantOrder.sellerId === currentUser?.uid);
        } else {
          console.log('❌ No merchant order found with parentOrderId:', orderId);
        }
      }
      
      if (merchantOrder) {
        
        // Get order items for this merchant order
        const orderItemsQuery = query(
          collection(db, 'order_items'),
          where('merchantOrderId', '==', merchantOrder.id)
        );
        const orderItemsSnap = await getDocs(orderItemsQuery);
        
        const orderItems = orderItemsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('🛒 Order items found:', orderItems);
        
        // Combine merchant order with items
        const orderWithItems = {
          ...merchantOrder,
          orderItems: orderItems
        };
        
        console.log('✅ Combined order data:', orderWithItems);
        setActualOrderData(orderWithItems);
      } else {
        console.log('❌ Merchant order not found for ID:', orderId);
        console.log('❌ This means the orderId from the conversation does not match any merchant_orders document');
        // Clear the order data when not found
        setActualOrderData(null);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      console.error('🔍 Failed orderId:', orderId);
      console.error('🔍 Current user ID:', currentUser?.uid);
      console.error('🔍 Error details:', error.message);
      // Clear order data when there's an error (permission denied, not found, etc.)
      setActualOrderData(null);
    }
  };

  // Use actual order data if available, otherwise fall back to hardcoded data
  console.log('🎯 Using order data - actualOrderData:', actualOrderData);
  
  // Group items by service date (same logic as Orders page)
  const getOrderStatusFromItems = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return 'PLACED';
    
    console.log('🔍 Debugging status determination for orderItems:', orderItems);
    
    // Group items by service date
    const serviceGroups = {};
    orderItems.forEach(item => {
      const serviceDate = item.serviceDate || 'unknown';
      console.log('📅 Item serviceDate:', serviceDate, 'Item status:', item.status);
      if (!serviceGroups[serviceDate]) {
        serviceGroups[serviceDate] = [];
      }
      serviceGroups[serviceDate].push(item);
    });
    
    console.log('📊 Service groups:', serviceGroups);
    
    // Get status from the first group (or you could determine which group to use)
    const firstGroupItems = Object.values(serviceGroups)[0] || [];
    console.log('🎯 First group items:', firstGroupItems);
    const status = getGroupStatus(firstGroupItems);
    console.log('✅ Final status:', status);
    return status;
  };

  // Get the appropriate action button text and action based on status
  const getActionButtonInfo = (status) => {
    switch (status) {
      case 'PLACED':
        return { text: 'Accept Order', action: 'accept-order' };
      case 'ACCEPTED':
        return { text: 'Mark as Ready', action: 'mark-ready' };
      case 'READY':
        return { text: 'Mark Complete', action: 'mark-complete' };
      case 'IN-PROGRESS':
        return { text: 'Mark as Ready', action: 'mark-ready' };
      case 'COMPLETED':
        return { text: 'View Details', action: 'view-details' };
      case 'CANCELLED':
        return { text: 'View Details', action: 'view-details' };
      default:
        return { text: 'Accept Order', action: 'accept-order' };
    }
  };
  
  const orderData = actualOrderData ? {
    id: actualOrderData.id,
    date: actualOrderData.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown',
    time: actualOrderData.createdAt?.toDate?.()?.toLocaleTimeString() || 'Unknown',
    fulfillment: actualOrderData.shippingAddress ? 'Delivery' : 'Pickup',
    items: actualOrderData.orderItems?.map(item => ({
      name: item.productSnapshot?.name || 'Unknown Product',
      quantity: parseInt(item.quantity) || 1,
      price: parseFloat(item.unitPrice) || 0
    })) || [],
    subtotal: parseFloat(actualOrderData.totals?.subtotal) || 0,
    fees: parseFloat(actualOrderData.totals?.fees) || 0,
    total: parseFloat(actualOrderData.totals?.total) || 0,
    customer: {
      name: actualOrderData.buyerName || selectedChatData?.customerName || location.state?.customerName || 'Customer',
      phone: actualOrderData.buyerPhone || '(555) 123-4567',
      address: actualOrderData.shippingAddress ? 
        `${actualOrderData.shippingAddress.street}, ${actualOrderData.shippingAddress.city}` :
        '123 Main St, 7:30 AM'
    },
    tags: [
      actualOrderData.shippingAddress ? 'Delivery' : 'Pickup',
      actualOrderData.paymentStatus === 'paid' ? 'Paid' : 'Unpaid',
      getOrderStatusFromItems(actualOrderData.orderItems) === 'PLACED' ? 'Placed' :
      getOrderStatusFromItems(actualOrderData.orderItems) === 'ACCEPTED' ? 'Accepted' :
      getOrderStatusFromItems(actualOrderData.orderItems) === 'IN-PROGRESS' ? 'In Progress' :
      getOrderStatusFromItems(actualOrderData.orderItems) === 'READY' ? 'Ready' :
      getOrderStatusFromItems(actualOrderData.orderItems) === 'COMPLETED' ? 'Completed' :
      getOrderStatusFromItems(actualOrderData.orderItems) === 'CANCELLED' ? 'Cancelled' :
      'Placed'
    ]
  } : {
    id: selectedChat || 'Unknown Order',
    date: 'Unknown',
    time: 'Unknown',
    fulfillment: 'Unknown',
    items: [],
    subtotal: 0.00,
    fees: 0.00,
    total: 0.00,
    customer: {
      name: selectedChatData?.customerName || location.state?.customerName || 'Customer',
      phone: 'Unknown',
      address: 'Unknown'
    },
    tags: ['No Data']
  };

  // Get the parent order ID for display (use parent order ID to match customer page)
  const displayOrderId = actualOrderData?.parentOrderId || (orderData?.id && orderData.id !== selectedChat ? orderData.id : null) || selectedChat || 'Unknown Order';

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch messages when component mounts
  useEffect(() => {
    if (currentUser?.uid) {
      console.log('Setting up messages subscription for seller:', currentUser.uid);
      
      const unsubscribe = subscribeToOrderMessages(currentUser.uid, async (messages) => {
        console.log('Received messages:', messages);
        const uniqueOrders = await getUniqueOrdersFromMessages(messages, currentUser.uid);
        console.log('Unique orders from messages:', uniqueOrders);
        
        // Update chats intelligently - merge with existing chats to preserve manually created ones
        setChats(prevChats => {
          const existingChatIds = new Set(prevChats.map(chat => chat.orderId));
          const newChats = uniqueOrders.filter(chat => !existingChatIds.has(chat.orderId));
          
          // If we have new chats, add them to the beginning
          if (newChats.length > 0) {
            console.log('🆕 Adding new conversations:', newChats.map(c => c.orderId));
            return [...newChats, ...prevChats];
          }
          
          // Otherwise, update existing chats with latest message info
          const updatedChats = prevChats.map(prevChat => {
            const updatedChat = uniqueOrders.find(chat => chat.orderId === prevChat.orderId);
            return updatedChat ? { ...prevChat, ...updatedChat } : prevChat;
          });
          
          return updatedChats;
        });
        
        // Note: Auto-selection will be handled in a separate useEffect after chats are populated
      });
      
      return () => unsubscribe();
    }
  }, [currentUser?.uid, location.state?.selectedOrderId, location.state?.customerId, location.state?.customerName, selectedChat, isMobile]);

  // Auto-select conversation when navigating from orders page
  // This runs after chats are populated to ensure we can find existing conversations
  useEffect(() => {
    if (location.state?.selectedOrderId && !selectedChat && chats.length > 0) {
      console.log('🔍 Auto-selecting conversation for merchantOrderId:', location.state.selectedOrderId);
      console.log('📋 Available chats:', chats.map(c => ({ orderId: c.orderId, customerName: c.customerName })));
      
      // Try to find existing conversation in chats array
      let targetChat = chats.find(chat => chat.orderId === location.state.selectedOrderId);
      
      // If not found, try to find merchant order and check parentOrderId
      if (!targetChat) {
        const findAndSelectConversation = async () => {
          try {
            // Get the merchant order to find parentOrderId
            const merchantOrderRef = doc(db, 'merchant_orders', location.state.selectedOrderId);
            const merchantOrderSnap = await getDoc(merchantOrderRef);
            
            let parentOrderId = null;
            
            if (merchantOrderSnap.exists()) {
              const merchantOrder = merchantOrderSnap.data();
              parentOrderId = merchantOrder.parentOrderId;
              console.log('📦 Found merchant order, parentOrderId:', parentOrderId);
              
              // Try to find chat with parentOrderId
              targetChat = chats.find(chat => chat.orderId === parentOrderId);
            }
            
            // If still not found, check for existing messages
            if (!targetChat) {
              const orderIdsToCheck = [
                location.state.selectedOrderId,
                parentOrderId
              ].filter(Boolean);
              
              console.log('🔍 Checking for messages with orderIds:', orderIdsToCheck);
              
              const messagesRef = collection(db, 'orderMessages');
              for (const orderId of orderIdsToCheck) {
                try {
                  const messagesQuery = query(
                    messagesRef,
                    where('orderId', '==', orderId),
                    limit(1)
                  );
                  const messagesSnap = await getDocs(messagesQuery);
                  if (!messagesSnap.empty) {
                    // Found messages - check if chat exists with this orderId
                    targetChat = chats.find(chat => chat.orderId === orderId);
                    if (targetChat) {
                      console.log('✅ Found existing conversation by message orderId:', orderId);
                      break;
                    }
                  }
                } catch (err) {
                  console.log('⚠️ Error checking messages for orderId:', orderId, err.message);
                }
              }
            }
            
            // Select the found conversation
            if (targetChat) {
              console.log('✅ Auto-selecting conversation:', targetChat.orderId);
              setSelectedChat(targetChat.orderId);
              if (isMobile) {
                setSelectedChatId(targetChat.orderId);
                setMobileView('chat');
              }
            } else {
              console.log('❌ No existing conversation found in chats array');
              // Don't create a new one here - let the subscription handle it
            }
          } catch (error) {
            console.error('❌ Error finding conversation:', error);
          }
        };
        
        findAndSelectConversation();
      } else {
        // Found exact match - select it immediately
        console.log('✅ Found exact match in chats, auto-selecting:', targetChat.orderId);
        setSelectedChat(targetChat.orderId);
        if (isMobile) {
          setSelectedChatId(targetChat.orderId);
          setMobileView('chat');
        }
      }
    }
  }, [location.state?.selectedOrderId, selectedChat, chats, currentUser?.uid, isMobile]);

  // Fetch order data when selectedChat changes
  useEffect(() => {
    if (selectedChat) {
      fetchOrderData(selectedChat);
    } else if (location.state?.selectedOrderId) {
      fetchOrderData(location.state.selectedOrderId);
    }
  }, [selectedChat, location.state?.selectedOrderId, currentUser?.uid]);

  // Load messages when selectedChat changes
  useEffect(() => {
    if (selectedChat && currentUser?.uid) {
      console.log('🔄 Loading messages for selected chat:', selectedChat);
      getOrderMessagesByOrderId(selectedChat).then(orderMessages => {
        console.log('📨 Messages loaded for chat:', selectedChat, 'Count:', orderMessages.length);
        const formattedMessages = orderMessages.map(msg => ({
          id: msg.id,
          sender: msg.senderId === currentUser?.uid ? 'seller' : 'customer',
          text: msg.message,
          timestamp: formatTimestamp(msg.timestamp)
        }));
        setMessages(formattedMessages);
      }).catch(error => {
        console.error('❌ Error loading messages for chat:', selectedChat, error);
      });
    }
  }, [selectedChat, currentUser?.uid]);

  const handleChatSelect = (chatId) => {
    setSelectedChat(chatId);
    
    // Messages will be loaded automatically by the useEffect when selectedChat changes
    // No need to duplicate the message loading logic here
    
    if (isMobile) {
      setMobileView('chat');
      setSelectedChatId(chatId);
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && !sending && selectedChat) {
      setSending(true);
      
      // Get customer ID from order data
      // Check multiple possible field names for buyer/customer ID
      const customerId = actualOrderData?.buyerId || 
                         actualOrderData?.customerId || 
                         actualOrderData?.userId ||
                         actualOrderData?.buyer_id ||
                         actualOrderData?.customer_id;
      
      if (!customerId) {
        console.error('❌ No customer/buyer ID found in order data:', actualOrderData);
      }
      
      const messageData = {
        orderId: selectedChat,
        senderId: currentUser?.uid,
        receiverId: customerId, // Use actual buyer/customer ID from order data
        senderName: currentUser?.displayName || 'Seller',
        senderType: 'seller',
        message: newMessage.trim()
      };
      
      // Create a temporary message for immediate display
      const tempMessage = {
        id: `temp-${Date.now()}`,
        sender: 'seller',
        text: newMessage.trim(),
        timestamp: formatTimestamp(new Date())
      };
      
      // Add the message to local state immediately
      setMessages(prev => [...prev, tempMessage]);
      setNewMessage('');
      
      sendOrderMessage(messageData).then((sentMessage) => {
        // Replace the temp message with the real one from Firestore
        setMessages(prev => prev.map(msg => 
          msg.id === tempMessage.id 
            ? {
                id: sentMessage.id,
                sender: 'seller',
                text: sentMessage.message,
                timestamp: formatTimestamp(sentMessage.timestamp)
              }
            : msg
        ));
        
        // Update the chat list to show the new message
        setChats(prev => prev.map(chat => 
          chat.orderId === selectedChat 
            ? {
                ...chat,
                lastMessage: sentMessage.message,
                lastTimestamp: sentMessage.timestamp
              }
            : chat
        ));
        
        setSending(false);
      }).catch(error => {
        console.error('Error sending message:', error);
        // Remove the temp message if sending failed
        setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
        setSending(false);
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted':
        return COLORS.PRIMARY_GRADIENT;
      case 'New':
        return '#6B7280';
      case 'Resolved':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };


  const handleOrderAction = async (action) => {
    if (!actualOrderData) {
      console.error('❌ No order data available for action:', action);
      alert('No order data available');
      return;
    }

    try {
      console.log('🔄 Executing order action:', action, 'for order:', actualOrderData.id);
      
      // Generate the correct groupId format: parentOrderId-sellerId-serviceDate
      const parentOrderId = actualOrderData.parentOrderId;
      const sellerId = currentUser.uid;
      
      // Get the service date from the first order item (assuming all items have same service date)
      const firstItem = actualOrderData.orderItems?.[0];
      const serviceDate = firstItem?.serviceDate;
      
      if (!parentOrderId || !serviceDate) {
        console.error('❌ Missing required data for groupId generation:', { parentOrderId, serviceDate });
        alert('Unable to update order: Missing order information');
        return;
      }
      
      const groupId = generateGroupId(parentOrderId, sellerId, serviceDate);
      console.log('🔍 Generated groupId:', groupId, 'from:', { parentOrderId, sellerId, serviceDate });
      
      // Map action to status
      let newStatus;
      switch (action) {
        case 'accept-order':
          newStatus = 'accepted';
          break;
        case 'mark-ready':
          newStatus = 'ready';
          break;
        case 'mark-complete':
          newStatus = 'completed';
          break;
        case 'refund':
          newStatus = 'cancelled';
          break;
        default:
          console.log('ℹ️ Action not implemented:', action);
          return;
      }
      
      console.log('🔄 Updating status to:', newStatus);
      const result = await updateGroupStatus(groupId, newStatus);
      console.log('✅ Update result:', result);
      
      if (result.success) {
        // Show success message
        const statusMessages = {
          'accepted': 'Order accepted!',
          'ready': 'Order marked as ready!',
          'completed': 'Order completed!',
          'cancelled': 'Order cancelled!'
        };
        
        alert(statusMessages[newStatus] || 'Order status updated successfully!');
        
        // Refresh the order data to reflect changes
        await fetchOrderData(actualOrderData.id);
      } else {
        alert(`Failed to update order status: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Order action error:', error);
      alert(`Failed to update order status: ${error.message}`);
    }
  };


  // Using date-fns formatTimestamp utility

  // Mobile navigation functions
  const goToChats = () => {
    setMobileView('chats');
    setSelectedChatId(null);
  };

  const goToOrder = () => {
    setMobileView('order');
  };

  const goToChat = () => {
    setMobileView('chat');
  };

  const filteredChats = chats.filter(chat =>
    chat.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.customerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SellerHeader />
      {/* Page Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #E5E7EB',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '72px'
      }}>
        {isMobile && mobileView !== 'chats' ? (
          <BackButton
            onClick={mobileView === 'chat' ? goToChats : goToChat}
            variant="standard"
            ariaLabel="Back"
          />
        ) : (
          <BackButton
            onClick={() => navigate('/seller-hub')}
            variant="standard"
            ariaLabel="Back to Seller Hub"
          />
        )}
        {isMobile && mobileView === 'chat' && (
          <button
            onClick={goToOrder}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Document size={20} color={COLORS.NEUTRAL_TEXT} />
          </button>
        )}
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        overflow: 'hidden',
        background: '#F9FAFB'
      }}>
        {/* Left Panel - Chat List */}
        {(!isMobile || mobileView === 'chats') && (
          <div style={{
            width: isMobile ? '100%' : '320px',
            background: 'white',
            borderRight: isMobile ? 'none' : '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column'
          }}>
          {/* Search Bar */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #F3F4F6'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search 
                size={20} 
                color="#9CA3AF" 
                style={{ position: 'absolute', left: '12px' }}
              />
              <input
                type="text"
                placeholder="Search orders, names..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#F9FAFB'
                }}
              />
            </div>
          </div>

          {/* Chat List */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6B7280'
              }}>
                Loading messages...
              </div>
            ) : filteredChats.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6B7280'
              }}>
                <div>No messages yet</div>
              </div>
            ) : (
            filteredChats.map(chat => (
              <div
                key={chat.orderId}
                onClick={() => handleChatSelect(chat.orderId)}
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #F3F4F6',
                  cursor: 'pointer',
                  background: selectedChat === chat.orderId ? 'rgba(139, 69, 19, 0.1)' : 'white',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        #{chat.orderId?.slice(-8).toUpperCase()} • {chat.customerName}
                      </span>
                      {chat.unread && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#3B82F6'
                        }} />
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {chat.lastMessage}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: '#9CA3AF'
                      }}>
                        {formatTimestamp(chat.lastTimestamp)}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        color: 'white',
                        background: getStatusColor('New') // Default status for now
                      }}>
                        {chat.status === 'PLACED' ? 'New' :
                         chat.status === 'ACCEPTED' ? 'Accepted' :
                         chat.status === 'READY' ? 'Ready' :
                         chat.status === 'COMPLETED' ? 'Complete' :
                         chat.status === 'CANCELLED' ? 'Cancelled' :
                         'New'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
            )}
          </div>
        </div>
        )}

        {/* Center Panel - Chat Window */}
        {(!isMobile || mobileView === 'chat') && (
          <div style={{
            flex: isMobile ? '1' : '1',
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            background: 'white'
          }}>
          {/* Chat Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: 600,
                color: COLORS.NEUTRAL_TEXT
              }}>
                {selectedChatData?.customerName}
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6B7280'
              }}>
                Order #{displayOrderId?.slice(-8).toUpperCase()}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {messages.map(message => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  justifyContent: message.sender === 'seller' ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  maxWidth: '70%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.sender === 'seller' ? 'flex-end' : 'flex-start'
                }}>
                                     <div style={{
                     padding: '12px 16px',
                     borderRadius: '18px',
                     background: message.sender === 'seller' ? 'rgba(139, 69, 19, 0.15)' : 'white',
                     border: message.sender === 'customer' ? '1px solid #E5E7EB' : 'none',
                     boxShadow: message.sender === 'customer' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                   }}>
                                         <p style={{
                       margin: 0,
                       fontSize: '14px',
                       color: message.sender === 'seller' ? COLORS.NEUTRAL_TEXT : COLORS.NEUTRAL_TEXT,
                       lineHeight: '1.4'
                     }}>
                       {message.text}
                     </p>
                  </div>
                  <span style={{
                    fontSize: '12px',
                    color: '#9CA3AF',
                    marginTop: '4px'
                  }}>
                    {message.timestamp}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #E5E7EB',
            background: 'white'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MoreCircle size={20} color="#9CA3AF" />
              </button>
              <div style={{
                flex: 1,
                position: 'relative'
              }}>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Write a message..."
                  disabled={sending}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '20px',
                    fontSize: '14px',
                    resize: 'none',
                    minHeight: '44px',
                    maxHeight: '120px',
                    fontFamily: 'inherit'
                  }}
                />
                <button style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Document size={16} color="#9CA3AF" />
                </button>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                style={{
                  width: '32px',
                  height: '32px',
                  background: newMessage.trim() && !sending ? COLORS.PRIMARY_GRADIENT : '#E5E7EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: newMessage.trim() && !sending ? 'pointer' : 'not-allowed',
                  opacity: newMessage.trim() && !sending ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
              >
                <ChevronRight 
                  size={16} 
                  primaryColor="white" 
                  set="bold"
                />
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Right Panel - Order Details */}
        {(!isMobile || mobileView === 'order') && selectedChat && (
          <div style={{
            width: isMobile ? '100%' : '320px',
            background: 'white',
            borderLeft: isMobile ? 'none' : '1px solid #E5E7EB',
            padding: '20px',
            overflow: 'auto'
          }}>
          {/* Order Summary */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: COLORS.NEUTRAL_TEXT
            }}>
              Order #{displayOrderId?.slice(-8).toUpperCase()}
            </h3>
            <p style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              color: '#6B7280'
            }}>
              Placed {orderData.date} • {orderData.fulfillment} • {orderData.time}
            </p>

            {/* No Order Data Warning */}
            {!actualOrderData && (
              <div style={{
                background: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#92400E',
                  fontWeight: 500
                }}>
                  ⚠️ Order data unavailable - Permission denied or order not found
                </p>
              </div>
            )}

            {/* Items */}
            <div style={{ marginBottom: '16px' }}>
              {orderData.items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT
                  }}>
                    {item.name}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT
                  }}>
                    {item.quantity} x ${(parseFloat(item.price) || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            {/* Financial Summary */}
            <div style={{
              borderTop: '1px solid #E5E7EB',
              paddingTop: '16px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#6B7280' }}>Subtotal</span>
                <span style={{ fontSize: '14px', color: COLORS.NEUTRAL_TEXT }}>${orderData.subtotal.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#6B7280' }}>Fees</span>
                <span style={{ fontSize: '14px', color: COLORS.NEUTRAL_TEXT }}>${orderData.fees.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: '1px solid #E5E7EB',
                paddingTop: '8px'
              }}>
                <span style={{ fontSize: '16px', fontWeight: 600, color: COLORS.NEUTRAL_TEXT }}>Total</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: COLORS.NEUTRAL_TEXT }}>${orderData.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Order Tags */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {orderData.tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#6B7280',
                    background: '#F3F4F6',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Seller Actions */}
          <div>
            <h4 style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: 600,
              color: COLORS.NEUTRAL_TEXT
            }}>
              Actions
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
                             <button
                 onClick={() => handleOrderAction(getActionButtonInfo(getOrderStatusFromItems(actualOrderData?.orderItems)).action)}
                 style={{
                   padding: '12px 16px',
                   background: COLORS.PRIMARY_GRADIENT,
                   color: 'white',
                   border: 'none',
                   borderRadius: '8px',
                   fontSize: '14px',
                   fontWeight: 500,
                   cursor: 'pointer'
                 }}
               >
                 {getActionButtonInfo(getOrderStatusFromItems(actualOrderData?.orderItems)).text}
               </button>
              <button
                onClick={() => handleOrderAction('refund')}
                style={{
                  padding: '12px 16px',
                  background: '#F3F4F6',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleOrderAction('view-order')}
                style={{
                  padding: '12px 16px',
                  background: '#F3F4F6',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Show size={16} />
                View order
              </button>
            </div>
          </div>
        </div>
        )}

      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && <BottomNavigation />}
    </div>
  );
}
